import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    MdBusiness,
    MdStore,
    MdLocationOn,
    MdInventory,
    MdTrendingDown,
    MdTrendingUp,
    MdSouthWest,
    MdNorthEast,
    MdLayers,
    MdDoneAll,
    MdSchedule,
    MdReplay,
    MdAssignmentTurnedIn,
    MdWarning,
    MdCampaign,
} from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatCard from '../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import DashboardSkeleton from '../../../components/common/skeletons/DashboardSkeleton';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { fetchDistributorDashboard } from '../../../services/Http/DistributorApi';
import { useCurrency } from '../../../context/CurrencyProvider';

const metricConfig = [
    { key: 'active_manufacturers_count', label: 'DistributorDashboard.metrics.activeManufacturers', icon: MdBusiness, format: 'number', color: 'manufacturer' },
    { key: 'active_retailers_count', label: 'DistributorDashboard.metrics.activeRetailers', icon: MdStore, format: 'number', color: 'retailers' },
    { key: 'active_venues_count', label: 'DistributorDashboard.metrics.activeVenues', icon: MdLocationOn, format: 'number', color: 'venues' },
    { key: 'sku_portfolio_size', label: 'DistributorDashboard.metrics.skuPortfolio', icon: MdInventory, format: 'number', color: 'users' },
    { key: 'inbound_value', label: 'DistributorDashboard.metrics.inboundValue', icon: MdTrendingDown, format: 'currency', color: 'revenueValue' },
    { key: 'inbound_quantity', label: 'DistributorDashboard.metrics.inboundQuantity', icon: MdSouthWest, format: 'number', color: 'soldItems' },
    { key: 'outbound_value', label: 'DistributorDashboard.metrics.outboundValue', icon: MdTrendingUp, format: 'currency', color: 'totalVolume' },
    { key: 'outbound_quantity', label: 'DistributorDashboard.metrics.outboundQuantity', icon: MdNorthEast, format: 'number', color: 'transactions' },
    { key: 'inventory_on_hand', label: 'DistributorDashboard.metrics.inventoryOnHand', icon: MdLayers, format: 'number', color: 'neutral' },
    { key: 'order_fill_rate', label: 'DistributorDashboard.metrics.orderFillRate', icon: MdDoneAll, format: 'percent', color: 'campaigns' },
    { key: 'average_fulfillment_time', label: 'DistributorDashboard.metrics.averageFulfillmentTime', icon: MdSchedule, format: 'days', color: 'info' },
    { key: 'returns_rate', label: 'DistributorDashboard.metrics.returnsRate', icon: MdReplay, format: 'percent', color: 'warning' },
    { key: 'open_support_cases', label: 'DistributorDashboard.metrics.openSupportCases', icon: MdAssignmentTurnedIn, format: 'number', color: 'danger' },
    { key: 'integration_alerts', label: 'DistributorDashboard.metrics.integrationAlerts', icon: MdWarning, format: 'number', color: 'alert' },
    { key: 'campaigns_supported', label: 'DistributorDashboard.metrics.campaignsSupported', icon: MdCampaign, format: 'number', color: 'campaigns' },
];

const highlightKeys = ['inbound_value', 'outbound_value', 'inventory_on_hand'];
const networkKeys = ['active_manufacturers_count', 'active_retailers_count', 'active_venues_count', 'sku_portfolio_size'];
const flowKeys = ['inbound_quantity', 'outbound_quantity', 'order_fill_rate', 'average_fulfillment_time'];
const healthKeys = ['returns_rate', 'open_support_cases', 'integration_alerts', 'campaigns_supported'];

const DistributorDashboard = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const metricLookup = useMemo(() => metricConfig.reduce((acc, cfg) => {
        acc[cfg.key] = cfg;
        return acc;
    }, {}), []);

    useEffect(() => {
        const loadDashboard = async () => {
            try {
                const response = await fetchDistributorDashboard();
                setMetrics(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDashboard();
    }, []);

    const insights = useMemo(() => ({
        topManufacturers: metrics?.top_manufacturers || [],
        topRetailers: metrics?.top_retailers || [],
        topRegions: metrics?.top_regions || [],
        actionItems: metrics?.action_items || [],
    }), [metrics]);

    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);



    if (error) {
        return <ErrorMessage>{t('DistributorDashboard.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('DistributorDashboard.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                {loading ? (
                    <DashboardSkeleton />
                ) : (
                    <>
                        <HighlightGrid>
                            {highlightKeys.map((key) => renderMetricCard(key, 'highlight', metrics, metricLookup, t, formatAmount))}
                        </HighlightGrid>

                        <Section>
                            <SectionHeader>
                                <SectionTitle>{t('DistributorDashboard.sections.network.title')}</SectionTitle>
                                <SectionCopy>{t('DistributorDashboard.sections.network.copy')}</SectionCopy>
                            </SectionHeader>
                            <SectionGrid>
                                {networkKeys.map((key) => renderMetricCard(key, 'standard', metrics, metricLookup, t, formatAmount))}
                            </SectionGrid>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <SectionTitle>{t('DistributorDashboard.sections.flow.title')}</SectionTitle>
                                <SectionCopy>{t('DistributorDashboard.sections.flow.copy')}</SectionCopy>
                            </SectionHeader>
                            <SectionGrid>
                                {flowKeys.map((key) => renderMetricCard(key, 'standard', metrics, metricLookup, t, formatAmount))}
                            </SectionGrid>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <SectionTitle>{t('DistributorDashboard.sections.health.title')}</SectionTitle>
                                <SectionCopy>{t('DistributorDashboard.sections.health.copy')}</SectionCopy>
                            </SectionHeader>
                            <SectionGrid>
                                {healthKeys.map((key) => renderMetricCard(key, 'standard', metrics, metricLookup, t, formatAmount))}
                            </SectionGrid>
                        </Section>

                        <InventoryInsightsLayout>
                            <InventoryPanel>
                                <InventoryHeader>
                                    <InventoryTitle>{t('DistributorDashboard.inventory.title')}</InventoryTitle>
                                    <InventorySub>{t('DistributorDashboard.inventory.copy')}</InventorySub>
                                </InventoryHeader>
                                <InventoryValue>{formatMetricValue(metrics?.inventory_on_hand, 'number', formatAmount)}</InventoryValue>
                                <InventorySplit>
                                    <SplitItem>
                                        <SplitLabel>{t('DistributorDashboard.inventory.inboundQuantity')}</SplitLabel>
                                        <SplitValue>{formatMetricValue(metrics?.inbound_quantity, 'number', formatAmount)}</SplitValue>
                                    </SplitItem>
                                    <SplitItem>
                                        <SplitLabel>{t('DistributorDashboard.inventory.outboundQuantity')}</SplitLabel>
                                        <SplitValue>{formatMetricValue(metrics?.outbound_quantity, 'number', formatAmount)}</SplitValue>
                                    </SplitItem>
                                </InventorySplit>
                            </InventoryPanel>

                            <InsightsWrapper>
                                <InsightsGrid>
                                    <InsightCard>
                                        <InsightTitle>{t('DistributorDashboard.insights.topManufacturers')}</InsightTitle>
                                        {renderList(insights.topManufacturers, 'manufacturer', formatAmount)}
                                    </InsightCard>
                                    <InsightCard>
                                        <InsightTitle>{t('DistributorDashboard.insights.topRetailers')}</InsightTitle>
                                        {renderList(insights.topRetailers, 'retailer', formatAmount)}
                                    </InsightCard>
                                    <InsightCard>
                                        <InsightTitle>{t('DistributorDashboard.insights.topRegions')}</InsightTitle>
                                        {renderList(insights.topRegions, 'region', formatAmount)}
                                    </InsightCard>
                                    <InsightCard>
                                        <InsightTitle>{t('DistributorDashboard.insights.actionItems')}</InsightTitle>
                                        <ActionList>
                                            {insights.actionItems.length === 0 && (
                                                <MutedText>{t('DistributorDashboard.insights.noActions')}</MutedText>
                                            )}
                                            {insights.actionItems.map((item, idx) => (
                                                <ActionItem key={`action-${idx}`}>{item}</ActionItem>
                                            ))}
                                        </ActionList>
                                    </InsightCard>
                                </InsightsGrid>
                            </InsightsWrapper>
                        </InventoryInsightsLayout>
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

const renderMetricCard = (key, variant, metrics, lookup, t, formatAmount) => {
    const config = lookup[key];
    if (!config) {
        return null;
    }
    const Icon = config.icon;
    const value = formatMetricValue(metrics?.[key], config.format, formatAmount);

    if (variant === 'highlight') {
        return (
            <HighlightCard key={key}>
                <div className="icon-wrap">
                    <Icon />
                </div>
                <div>
                    <HighlightLabel>{t(config.label)}</HighlightLabel>
                    <HighlightValue>{value}</HighlightValue>
                </div>
            </HighlightCard>
        );
    }

    return (
        <StatCard
            key={key}
            icon={<Icon className="icon" />}
            title={t(config.label)}
            value={value}
            color={config.color}
        />
    );
};

const formatMetricValue = (value, formatType = 'number', formatAmount = (v) => v) => {
    const metric = typeof value === 'object' && value !== null ? value : { value };
    const rawValue = metric.value;

    if (rawValue === null || rawValue === undefined) {
        return '0';
    }

    const numeric = Number(rawValue);
    if (Number.isNaN(numeric)) {
        return rawValue;
    }

    switch (formatType) {
        case 'currency':
            return formatAmount(
                numeric,
                metric.localAmount,
                metric.localCurrencyId,
                metric.localCurrencyCode
            );
        case 'percent':
            return `${numeric.toFixed(2)}%`;
        case 'days':
            return `${numeric.toFixed(2)} d`;
        default:
            if (Number.isInteger(numeric)) {
                return numeric.toLocaleString();
            }
            return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
};

const renderList = (items = [], labelKey, formatAmount) => {
    if (!items || items.length === 0) {
        return <MutedText>–</MutedText>;
    }

    return (
        <InsightList>
            {items.map((item, idx) => (
                <InsightListItem key={`${labelKey}-${idx}`}>
                    <ListLabel>{item[labelKey] || item}</ListLabel>
                    {(item.value !== undefined || item.transactions !== undefined) && (
                        <ListValue>
                            {item.value !== undefined && formatMetricValue({
                                value: item.value,
                                localAmount: item.value_local,
                                localCurrencyId: item.seller_currency_id ?? item.currency_id,
                                localCurrencyCode: item.seller_currency_code ?? item.currency_code,
                            }, item.format || 'currency', formatAmount)}
                            {item.transactions !== undefined && (
                                <span className="tx-count">({item.transactions} tx)</span>
                            )}
                        </ListValue>
                    )}
                </InsightListItem>
            ))}
        </InsightList>
    );
};

const HighlightGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const HighlightCard = styled.div`
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.12), rgba(130, 22, 146, 0.04));
    border-radius: 12px;
    padding: 1.125rem;
    display: flex;
    gap: 1rem;
    align-items: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);

    .icon-wrap {
        background: rgba(130, 22, 146, 0.1);
        border-radius: 50%;
        width: 46px;
        height: 46px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        color: #821692;
    }
`;

const HighlightLabel = styled.div`
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const HighlightValue = styled.div`
    font-size: 1.6rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors?.textPrimary || '#1d1d1d'};
`;

const Section = styled.section`
    margin-top: 2rem;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin-bottom: 1.125rem;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1.3rem;
    color: ${({ theme }) => theme.colors?.brand || '#821692'};
`;

const SectionCopy = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#555'};
    max-width: 640px;
`;

const SectionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const InventoryInsightsLayout = styled.div`
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.5rem;
`;

const InventoryPanel = styled.div`
    background: radial-gradient(circle at top right, rgba(130, 22, 146, 0.1), rgba(0, 0, 0, 0));
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
`;

const InventoryHeader = styled.div`
    margin-bottom: 0.75rem;
`;

const InventoryTitle = styled.h4`
    margin: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors?.brand || '#821692'};
`;

const InventorySub = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#555'};
    font-size: 0.875rem;
`;

const InventoryValue = styled.div`
    font-size: 2.4rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors?.textPrimary || '#1d1d1d'};
`;

const InventorySplit = styled.div`
    margin-top: 1.125rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
`;

const SplitItem = styled.div`
    background: rgba(255, 255, 255, 0.7);
    border-radius: 10px;
    padding: 0.75rem;
    border: 1px solid rgba(130, 22, 146, 0.15);
`;

const SplitLabel = styled.p`
    margin: 0;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const SplitValue = styled.p`
    margin: 0.25rem 0 0;
    font-weight: 600;
    font-size: 1.1rem;
`;

const InsightsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const InsightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: ${({ theme }) => theme.spacing?.medium || '1.25rem'};
`;

const InsightCard = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: ${({ theme }) => theme.spacing?.medium || '1.25rem'};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const InsightTitle = styled.h4`
    margin-bottom: ${({ theme }) => theme.spacing?.small || '0.625rem'};
    color: ${({ theme }) => theme.colors?.brand || '#821692'};
`;

const MutedText = styled.p`
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    margin: 0;
`;

const InsightList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const InsightListItem = styled.li`
    display: flex;
    justify-content: space-between;
    padding: 0.375rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
        border-bottom: none;
    }
`;

const ListLabel = styled.span`
    font-weight: 600;
`;

const ListValue = styled.span`
    font-weight: 500;
    color: ${({ theme }) => theme.colors?.textSecondary || '#555'};

    .tx-count {
        margin-left: 0.375rem;
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors?.textMuted || '#888'};
    }
`;

const ActionList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const ActionItem = styled.li`
    background: rgba(130, 22, 146, 0.08);
    padding: 0.625rem 0.75rem;
    border-radius: 6px;
    font-size: 0.95rem;
`;

export default DistributorDashboard;
